import React, { useEffect, useRef, useState } from "react";
import { proxify, proxifySrcSet } from "@/lib/imgProxy";

/**
 * CrossfadeImage — true crossfade between consecutive `src` props.
 *
 * Why a dedicated component?
 *   The hero slideshow previously used `<img loading="lazy" decoding="async" key={src} … className="hero-fade" />`.
 *   Changing the key remounts the <img loading="lazy" decoding="async">, so the browser briefly paints a blank
 *   element while the new image fetches — the visual "lafa lafi" the user saw.
 *
 *   Here we keep two layers stacked: the previous src is held while the new
 *   src fetches AND decodes; only when the new layer fires onLoad do we flip
 *   its opacity to 1 (the previous layer fades to 0). Result: no white flash
 *   on slow mobile networks, no remount, butter-smooth.
 *
 *   `loading="eager"` is used for ALL layers (not just the first) because
 *   slideshow images are guaranteed to be in the viewport. Marking them lazy
 *   was the main reason mobile users saw 4-7 seconds of blank box during slide
 *   changes — the browser was deprioritizing the fetch.
 *
 * Props:
 *   - src         : current image URL
 *   - alt         : alt text
 *   - className   : applied to the wrapping container (use to size the box)
 *   - imgClassName: applied to each <img loading="lazy" decoding="async"> (e.g. for object-cover + zoom hover)
 *   - kenBurns    : if true, the visible layer slowly drifts/scales (cinematic)
 *   - duration    : crossfade ms (default 700)
 *   - eager       : keep for backward compat — has no effect (all layers eager).
 */
export default function CrossfadeImage({
  src,
  alt = "",
  className = "",
  imgClassName = "",
  kenBurns = false,
  duration = 700,
  eager = false,
}) {
  const [layers, setLayers] = useState(() => (src ? [{ src, key: 0, loaded: true }] : []));
  const counter = useRef(0);

  useEffect(() => {
    if (!src) return;
    setLayers((prev) => {
      if (prev.length && prev[prev.length - 1].src === src) return prev;
      counter.current += 1;
      // Keep at most the previous layer (visible) + the incoming one (loading).
      const tail = prev.length ? [prev[prev.length - 1]] : [];
      return [...tail, { src, key: counter.current, loaded: false }];
    });
  }, [src]);

  // When the top layer's onLoad fires we mark it loaded. After the fade completes,
  // drop the bottom layer to free GPU memory.
  const markLoaded = (key) => {
    setLayers((prev) => prev.map((l) => (l.key === key ? { ...l, loaded: true } : l)));
    setTimeout(() => {
      setLayers((prev) => (prev.length > 1 ? [prev[prev.length - 1]] : prev));
    }, duration + 50);
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {layers.map((layer, i) => {
        const isTop = i === layers.length - 1;
        // Top layer is visible only once its image has decoded. Bottom (previous)
        // layer stays at opacity 1 until the top is ready, eliminating the blank.
        const visible = isTop ? layer.loaded : !layers[layers.length - 1]?.loaded;
        const proxied = proxify(layer.src, 1600);
        const srcSet = proxifySrcSet(layer.src);
        return (
          <img
            key={layer.key}
            src={proxied}
            srcSet={srcSet}
            sizes="(max-width: 768px) 100vw, 60vw"
            alt={alt}
            width="1200"
            height="900"
            loading={eager && isTop ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={eager && isTop ? "high" : "low"}
            onLoad={() => { if (isTop && !layer.loaded) markLoaded(layer.key); }}
            style={{
              opacity: visible ? 1 : 0,
              transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`,
              animation: kenBurns && isTop && layer.loaded ? "kenBurnsDrift 14s ease-out both" : undefined,
              willChange: "opacity, transform",
            }}
            className={`absolute inset-0 w-full h-full object-cover ${imgClassName}`}
          />
        );
      })}
    </div>
  );
}
